import commonInterceptors from '@/interceptors/commonInterceptors.interceptor.js';
import pixAxios from '@/axios/pixAxios';
import pixAxiosSyncRequests from '@/axios/pixAxiosSyncRequests';
import axios from 'axios';
import jllOktaAuthManager from 'jll-okta-auth-manager';
import Console from '@/shared/console';
import pixRouter from '@/services/routing/router.js';

/* Be careful when using Console as login executes before setting Console's this.showlogs
 * so Console.error and Console.log might not show up on
 */

/* Obtiene token de autorización y lo guarda en localStorage y vuex */
const oktaLogin = async function ({ commit, state, dispatch, redirectStorageKey, pixAuthTokenKey }) {
  Console.log('[Store Action] - Login init');

  try {
    const oktaAuthToken = await _getOktaAuthToken(pixAuthTokenKey);
    _storeOktaAuthToken(oktaAuthToken, pixAuthTokenKey);

    const pixAuthToken = await _getPixAuthToken(oktaAuthToken, commit);
    _storePixAuthToken(pixAuthToken, commit);

    _fetchUserSettings({ state, dispatch });

    const redirectURL = localStorage.getItem(redirectStorageKey);

    if (redirectURL) {
      localStorage.removeItem(redirectStorageKey); // Recogemos la url que había antes del login
      pixRouter.push(redirectURL);
    }

    Console.log('[Store Action] - Login end');

    return pixAuthToken;
  } catch (error) {
    console.error('PIX auth error', error);
  }
};

const _getOktaAuthToken = async function (pixAuthTokenKey) {
  let oktaToken = JSON.parse(localStorage.getItem(pixAuthTokenKey));

  /* Si ya tengo un token guardado en el localStorage no necesito lanzar login contra okta */
  if (!oktaToken) {
    oktaToken = await _getNewOktaToken();
  }

  return oktaToken;
};

const _storeOktaAuthToken = function (oktaToken, pixAuthTokenKey) {
  if (oktaToken) {
    localStorage.setItem(pixAuthTokenKey, JSON.stringify(oktaToken));
  }
};

const _sanitizeToken = function (oktaToken) {
  let token = oktaToken;
  delete token.authorizeUrl;
  delete token.issuer;
  return token;
};

const _getPixAuthToken = async function (oktaAuthToken) {
  /* Llamamos al login de backend para rellenar el token con datos de autenticación como roles, nombre e instancia */
  const sanitizedToken = _sanitizeToken(oktaAuthToken);

  const { data: authJWT } = await axios.post('api/Login', sanitizedToken);
  const pixAuthToken = authJWT.token;
  return pixAuthToken;
};

const _storePixAuthToken = function (pixAuthToken, commit) {
  const idToken = JSON.parse(pixAuthToken.access_token).idToken;
  _setAuthHeaders(idToken);
  commit('setToken', pixAuthToken); // Se guarda en el store de vuex
};

const oktaLogout = async function ({ commit, pixAuthTokenKey }) {
  commit('setToken', null);

  await jllOktaAuthManager.logout();

  _deleteAuthHeaders();

  localStorage.clear();
  sessionStorage.clear();
  window.location.assign('/landing');
};

const _deleteAuthHeaders = function () {
  commonInterceptors.deleteAuthHeader(axios);
  commonInterceptors.deleteAuthHeader(pixAxios);
  commonInterceptors.deleteAuthHeader(pixAxiosSyncRequests);
};

const _setAuthHeaders = function (idToken) {
  /* Se fija en el header de autorización para todas las llamadas de aquí en adelante en las distintas instancias de axios */
  commonInterceptors.setAuthHeader(axios, idToken);
  commonInterceptors.setAuthHeader(pixAxios, idToken);
  commonInterceptors.setAuthHeader(pixAxiosSyncRequests, idToken);
};

const _fetchUserSettings = function ({ state, dispatch }) {
  if (!state.userSettings) {
    dispatch('paramListModule/loadParamListByParamId', 885, { root: true }); // User_Settings_Date_Format -> formatos de fecha
    dispatch('getUserSettings');
  }
};

const _getNewOktaToken = async function () {
  let oktaToken = null;

  // const oktaSession = await jllOktaAuthManager.getCurrentSession();

  // if (oktaSession && oktaSession.status === 'ACTIVE') {
  //   /* hay una sesión de okta abierta por lo tanto podemos recoger el token haciendo sign in with redirect */
  //   oktaToken = await jllOktaAuthManager.getTokenFromSession();
  // } else {
  await jllOktaAuthManager.login();
  oktaToken = await jllOktaAuthManager.getToken();
  // }

  return oktaToken;
};

// https://stackoverflow.com/a/51600050/15328250
const oktaRenew = function ({ state, commit, pixAuthTokenKey }) {
  if (state.isRenewingToken) {
    // Cuando ya se está refrescando el token, se devuelve la promesa que lo está refrescando (en lugar de hacer otra llamada para refrescar el token de nuevo)
    return state.refreshingTokenPromise;
  } else {
    // Cuando no se está refrescando el token, se lanza llamada para obtener uno nuevo y se guarda en el browser (localstorage y cabeceras axios)
    commit('setIsRenewingToken', true);
    const refreshingTokenPromise = jllOktaAuthManager.renew().then(async (oktaAuthToken) => {
      _storeOktaAuthToken(oktaAuthToken, pixAuthTokenKey);
      const pixAuthToken = await _getPixAuthToken(oktaAuthToken);
      _storePixAuthToken(pixAuthToken, commit);
      commit('setRefreshingTokenPromise', null);
      commit('setIsRenewingToken', false);
      return pixAuthToken;
    });
    commit('setRefreshingTokenPromise', refreshingTokenPromise);

    // Se devuelve la promesa que devuelve el nuevo token para luego se utilizado en el retry de la llamada que tenía el token caducado
    return refreshingTokenPromise;
  }
};

export { oktaLogin, oktaLogout, oktaRenew };
